import { useState, useEffect } from 'react';
import { 
  X, User, Mail, Phone, MapPin, Headphones, Edit, Trash2 
} from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';

const UserModal = ({ user, type, onClose, onSave, lang }) => {
  const { t } = useTranslation(lang);
  const [modalType, setType] = useState(type); // 'view', 'edit', 'add'
const [formData, setFormData] = useState({
  name: '',
  email: '',
  phone: '',
  address: '',
  support_service_id: 1  // défaut
});
  // const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

useEffect(() => {
  if (user && modalType !== 'add') {
    setFormData({
      name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || '',
      address: user.address || '',
      support_service_id: user.support_service_id || 1
    });
  } else {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      support_service_id: 1
    });
  }
}, [user, modalType]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

    const handlePhoneChange = (value) => {
    // Permettre uniquement les chiffres, espaces, +, (), -
    const cleanedValue = value.replace(/[^\d\s\+\-\(\)]/g, '');
    handleChange('phone', cleanedValue);
  };

const validateForm = () => {
  const newErrors = {};

  if (!formData.name.trim()) {
    newErrors.name = t('nameRequired');
  }

  if (!formData.email.trim()) {
    newErrors.email = t('emailRequired');
  } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
    newErrors.email = t('invalidEmail');
  }

  if (!formData.phone.trim()) {
    newErrors.phone = t('phoneRequired');
  }

  if (!formData.address.trim()) {
    newErrors.address = t('addressRequired');
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

const handleSubmit = (e) => {
  e.preventDefault();
  
  if (!validateForm()) return;

  // Préparer les données pour l'API
  const userData = {
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    address: formData.address,
    support_service_id: parseInt(formData.support_service_id, 10)
  };

  // Appeler onSave avec les données formatées
  onSave(userData);
};

  const isViewMode = modalType === 'view';

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '200px'
    },
    modal: {
      background: 'white',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '900px',
      minWidth: '300px',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '24px',
      borderBottom: '1px solid #e5e7eb'
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      borderRadius: '4px',
      color: '#6b7280'
    },
    content: {
      padding: '24px'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151'
    },
    input: {
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      transition: 'all 0.2s'
    },
    inputError: {
      borderColor: '#ef4444'
    },
    inputDisabled: {
      backgroundColor: '#f9fafb',
      color: '#6b7280',
      cursor: 'not-allowed'
    },
  select: {
    padding: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: 'white',
    cursor: 'pointer'
  },
    passwordContainer: {
      position: 'relative'
    },
    passwordToggle: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280'
    },
    error: {
      color: '#ef4444',
      fontSize: '12px',
      marginTop: '4px'
    },
    helpText: {
      color: '#6b7280',
      fontSize: '12px',
      marginTop: '4px',
      fontStyle: 'italic'
    },
    footer: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      padding: '24px',
      borderTop: '1px solid #e5e7eb'
    },
    button: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    buttonPrimary: {
      backgroundColor: '#7c3aed',
      color: 'white'
    },
    buttonSecondary: {
      backgroundColor: '#6b7280',
      color: 'white'
    }
  };

return (
  <div style={styles.overlay} onClick={onClose}>
    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.title}>
          <User size={20} />
          {modalType === 'add' && t('addUser')}
          {modalType === 'edit' && t('editUser')}
          {modalType === 'view' && t('userDetails')}
        </div>
        <button 
          style={styles.closeButton}
          onClick={onClose}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div style={styles.content}>
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Nom */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>
              <User size={16} />
              {t('name')} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              style={{
                ...styles.input,
                ...(errors.name && styles.inputError),
                ...(isViewMode && styles.inputDisabled)
              }}
              disabled={isViewMode}
              placeholder={t('enterName')}
            />
            {errors.name && <div style={styles.error}>{errors.name}</div>}
          </div>

          {/* Email */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>
              <Mail size={16} />
              {t('email')} *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              style={{
                ...styles.input,
                ...(errors.email && styles.inputError),
                ...(isViewMode && styles.inputDisabled)
              }}
              disabled={isViewMode}
              placeholder={t('enterEmail')}
            />
            {errors.email && <div style={styles.error}>{errors.email}</div>}
          </div>

          {/* Téléphone */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>
              <Phone size={16} />
              {t('phone')} *
            </label>
            <input
              type="tel"
              value={formData.phone}
              maxLength={12}
              onChange={(e) => handlePhoneChange(e.target.value)}
              style={{
                ...styles.input,
                ...(errors.phone && styles.inputError),
                ...(isViewMode && styles.inputDisabled)
              }}
              disabled={isViewMode}
              placeholder={t('enterPhone')}
            />
            {errors.phone && <div style={styles.error}>{errors.phone}</div>}
          </div>

          {/* Adresse */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>
              <MapPin size={16} />
              {t('address')} *
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              style={{
                ...styles.input,
                ...(errors.address && styles.inputError),
                ...(isViewMode && styles.inputDisabled)
              }}
              disabled={isViewMode}
              placeholder={t('enterAddress')}
            />
            {errors.address && <div style={styles.error}>{errors.address}</div>}
          </div>

          {/* Service de support (select) */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>
              <Headphones size={16} />
              {t('supportService')} *
            </label>
            <select
              value={formData.support_service_id}
              onChange={(e) => handleChange('support_service_id', e.target.value)}
              style={{
                ...styles.select,
                ...(isViewMode && styles.inputDisabled)
              }}
              disabled={isViewMode}
            >
              <option value="1">{t('yes')}</option>
              {/* <option value="0">{t('no')}</option> */}
            </select>
          </div>
        </form>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        {isViewMode ? (
          <>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                style={{ 
                  ...styles.button, 
                  backgroundColor: '#3b82f6',
                  color: 'white'
                }}
                onClick={() => setType('edit')}
              >
                <Edit size={14} />
                {t('edit')}
              </button>
              <button
                type="button"
                style={{ 
                  ...styles.button, 
                  backgroundColor: '#ef4444',
                  color: 'white'
                }}
                onClick={() => {
                  if (window.confirm(t('confirmDeleteText', { name: formData.name }))) {
                    // À connecter avec la fonction de suppression du parent
                  }
                }}
              >
                <Trash2 size={14} />
                {t('delete')}
              </button>
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              style={{ ...styles.button, ...styles.buttonSecondary }}
              onClick={onClose}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              style={{ ...styles.button, ...styles.buttonPrimary }}
              onClick={handleSubmit}
            >
              {modalType === 'add' ? t('add') : t('save')}
            </button>
          </>
        )}
      </div>
    </div>
  </div>
);
};

export default UserModal;